import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserById, getUserByUsername, forceNetworkReset } from '../firebase.js';
import toast from 'react-hot-toast';
import { Box, Typography, Button, CircularProgress, Paper, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Refresh as RefreshIcon, Home as HomeIcon } from '@mui/icons-material';

// Environment detection
const isDevMode = process.env.NODE_ENV === 'development';

export default function UserRedirect() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isResetting, setIsResetting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorDetails, setErrorDetails] = useState(null);

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      if (error) {
        setRetryCount(prev => prev + 1);
        setError(null);
      }
    };
    
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [error]);

  const forceReconnect = async () => {
    setIsResetting(true);
    toast.loading("Resetting connection...", { id: "connection-reset" });
    
    try {
      console.log("🔄 UserRedirect: Attempting to force network reset");
      
      // First check if we're online at the browser level
      if (!navigator.onLine) {
        toast.error("Device is offline. Please check your connection.", { id: "connection-reset" });
        throw new Error("Device is offline");
      }
      
      // Attempt to reset Firebase connection
      const result = await forceNetworkReset();
      
      if (result.success) {
        console.log("✅ UserRedirect: Connection reset successful");
        toast.success("Connection reset successful!", { id: "connection-reset" });
        
        // Reset error state
        setError(null);
        setErrorDetails(null);
        
        // Trigger a retry
        setRetryCount(prev => prev + 1);
      } else {
        console.error("❌ UserRedirect: Connection reset failed", result.error);
        toast.error("Connection reset failed. Try refreshing the page.", { id: "connection-reset" });
        
        // Store details about the error
        setErrorDetails(prevDetails => ({
          ...prevDetails,
          connectionResetAttempt: new Date().toISOString(),
          connectionResetError: result.error?.message || "Unknown error during connection reset"
        }));
      }
    } catch (err) {
      console.error("❌ UserRedirect: Connection reset error", err);
      toast.error("Connection reset failed", { id: "connection-reset" });
      setErrorDetails(prevDetails => ({
        ...prevDetails,
        connectionResetError: err.message || "Unknown error during connection reset"
      }));
    } finally {
      setIsResetting(false);
    }
  };

  // Resolve UID to username, or username to UID, with multiple fallback approaches
  const resolveUser = async (identifier) => {
    console.log(`🔍 UserRedirect: Resolving user with identifier: ${identifier}`);
    
    const isUid = identifier && identifier.length > 20; // Heuristic to check if it's a UID (Firebase UIDs are usually longer)
    
    // Approach 1: Direct document lookup if it's a UID
    if (isUid) {
      try {
        console.log("🔍 Attempting direct user lookup by UID");
        const userData = await getUserById(identifier);
        if (userData) {
          console.log(`✅ Found user ${userData.username || 'with no username'} by UID: ${identifier}`);
          return userData;
        } else {
          console.log(`⚠️ No user found with UID: ${identifier}`);
        }
      } catch (uidError) {
        console.error("❌ UID lookup failed:", uidError);
        
        // Check for App Check errors (403 Forbidden)
        if (uidError.code === 'permission-denied' || uidError.message?.includes('permission')) {
          throw new Error("App Check validation failed. The server rejected this request due to security constraints.");
        }
        
        if (isDevMode) console.log("UID lookup error details:", uidError);
      }
    }
    
    // Approach 2: Check if input is a username
    if (!isUid || !identifier) {
      try {
        const usernameToCheck = isUid ? null : identifier;
        
        // If we don't have a username to check yet, try to find one from the UID
        if (!usernameToCheck && isUid) {
          // Already tried direct UID lookup above with no success
          console.log("⚠️ Skipping username lookup as we already tried UID lookup");
          return null;
        }
        
        console.log(`🔍 Attempting username lookup for: ${usernameToCheck}`);
        const userData = await getUserByUsername(usernameToCheck);
        
        if (userData) {
          console.log(`✅ Found user ${userData.username || 'with no username'} by username lookup: ${usernameToCheck}`);
          return userData;
        } else {
          console.log(`⚠️ No user found with username: ${usernameToCheck}`);
        }
      } catch (usernameError) {
        console.error("❌ Username lookup failed:", usernameError);
        
        // Check for App Check errors (403 Forbidden)
        if (usernameError.code === 'permission-denied' || usernameError.message?.includes('permission')) {
          throw new Error("App Check validation failed. The server rejected this request due to security constraints.");
        }
        
        if (isDevMode) console.log("Username lookup error details:", usernameError);
      }
    }
    
    // No user found with any method
    console.error(`❌ UserRedirect: User not found with identifier: ${identifier}`);
    return null;
  };

  // Main effect for user lookup with timeout handling
  useEffect(() => {
    let isMounted = true;
    let timeoutId;
    let abortController = new AbortController();

    if (!uid) {
      setIsLoading(false);
      setError("No user ID provided");
      return;
    }

    setIsLoading(true);
    setError(null);

    const fetchAndRedirect = async () => {
      // Clear any previous timeout
      if (timeoutId) clearTimeout(timeoutId);
      
      // Set a timeout for the whole operation
      const operationTimeout = setTimeout(() => {
        if (isMounted) {
          abortController.abort("Operation timed out");
          setError("Request timed out. The server might be unavailable or there's a network issue.");
          setIsLoading(false);
        }
      }, 15000);
      
      try {
        console.log(`🔄 UserRedirect: Processing user with identifier: ${uid}`);
        console.log(`🌐 Network status: ${navigator.onLine ? "Online" : "Offline"}`);
        console.log(`🔥 Environment: ${process.env.NODE_ENV}`);
        
        // Add an extra check for network connectivity
        if (!navigator.onLine) {
          throw new Error("Device is offline");
        }
        
        // Use our enhanced user resolution function with multiple approaches
        const userData = await resolveUser(uid);
        
        // Clear the timeout since the operation completed
        clearTimeout(operationTimeout);
        
        if (!isMounted) return;
        
        if (userData) {
          if (userData.username) {
            console.log(`✅ UserRedirect: Redirecting to profile: ${userData.username}`);
            navigate(`/u/profile/${userData.username}`, { replace: true });
          } else if (userData.id) {
            // Handle users with no username (rare edge case)
            console.warn("⚠️ User exists but has no username field, using ID instead");
            navigate(`/u/profile/${userData.id}`, { replace: true });
          } else {
            console.error("⚠️ User exists but has no identifiable information");
            navigate('/not-found', { replace: true, state: { reason: 'incomplete-profile' } });
          }
        } else {
          console.error("❌ User not found with identifier:", uid);
          navigate('/not-found', { replace: true, state: { reason: 'user-not-found' } });
        }
      } catch (error) {
        // Clear the timeout since the operation completed (with an error)
        clearTimeout(operationTimeout);
        
        if (!isMounted) return;
        
        console.error("❌ UserRedirect: Error redirecting:", error);
        setIsLoading(false);
        
        // Store detailed error information for debugging
        setErrorDetails({
          message: error.message,
          code: error.code,
          name: error.name,
          stack: error.stack?.split('\n')[0] || 'No stack trace',
          timestamp: new Date().toISOString(),
          environment: process.env.NODE_ENV,
          online: navigator.onLine
        });
        
        // Set user-friendly error message based on the error
        if (error.name === 'AbortError' || error.message?.includes('timed out')) {
          setError("The request timed out. Check your internet connection and try again.");
        } else if (!navigator.onLine || error.message?.includes('offline')) {
          setError("Failed to get document because the client is offline. Please check your connection and try again.");
        } else if (error.message?.includes('permission') || error.code === 'permission-denied') {
          setError("Access denied. This request was rejected for security reasons.");
        } else if (error.message?.includes('CORS') || error.message?.includes('cross-origin') || error.code === 'failed-precondition') {
          setError("Cross-origin request blocked. This could be due to browser security settings or network configuration.");
          } else {
          setError("Unable to connect to Firebase. The server might be unavailable or there's a network issue.");
        }
        
        // Retry automatically for network errors, but with exponential backoff
        if (retryCount < 3 && navigator.onLine && isMounted) {
          const nextRetry = retryCount + 1;
          const delay = 1500 * Math.pow(2, retryCount); // Exponential backoff: 1.5s, 3s, 6s
          
          console.log(`⏱️ UserRedirect: Will retry automatically in ${delay/1000} seconds (attempt ${nextRetry})`);
          
          timeoutId = setTimeout(() => {
            if (isMounted) setRetryCount(nextRetry);
          }, delay);
        }
      }
    };

    fetchAndRedirect();
    
    // Cleanup function
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      abortController.abort("Component unmounted");
    };
  }, [uid, navigate, retryCount]);

  // Error UI component with Material UI
  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <Paper elevation={3} sx={{ maxWidth: 500, width: '100%', p: 4, backgroundColor: '#1a1a1a' }}>
          <Typography variant="h5" component="h2" gutterBottom color="error">
            Error Redirecting
          </Typography>
          
          <Typography variant="body1" gutterBottom color="error.light" sx={{ mb: 3 }}>
            {error}
          </Typography>
          
          {errorDetails && (
            <Accordion sx={{ mb: 3, backgroundColor: '#222', color: '#ccc' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  Technical Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box component="pre" sx={{ 
                  whiteSpace: 'pre-wrap', 
                  fontSize: '12px',
                  backgroundColor: '#333',
                  p: 2,
                  borderRadius: 1,
                  overflowX: 'auto'
                }}>
                  {JSON.stringify(errorDetails, null, 2)}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={() => setRetryCount(prev => prev + 1)}
              disabled={isResetting || isLoading}
              fullWidth
            >
              Retry
            </Button>
            
            <Button
              variant="contained"
              color="warning"
              startIcon={<RefreshIcon />}
              onClick={forceReconnect}
              disabled={isResetting || isLoading}
              fullWidth
            >
              {isResetting ? 'Resetting Connection...' : 'Force Connection Reset'}
            </Button>
            
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/', { replace: true })}
              fullWidth
          >
            Go Home
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Loading UI component with Material UI
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '70vh',
      p: 3
    }}>
      <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
      <Typography variant="h6" component="p" color="primary.light">
        Redirecting{retryCount > 0 ? ` (Attempt ${retryCount + 1})` : ''}...
      </Typography>
      {isOffline && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          Warning: You appear to be offline. This operation may fail.
        </Typography>
      )}
    </Box>
  );
} 